import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { PageContent } from '../components/layout/PageContent';
import { federationApi, rankingFederativoApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ContagemMedalhas, SeloPosicao } from '../components/common/Medalhas';
import {
  Award,
  Loader2,
  Building2,
  Settings2,
  Users,
  Trophy,
  Swords,
  TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { EstadoVazio } from '../components/common/Estados';

/**
 * FASE C — Ranking Federativo acumulado (Atletas e Equipes) por
 * Federação/Liga e temporada. Agregação pura do ledger do Ranking
 * Federativo, isolada por federação e temporada.
 */

/** Delega para a peça compartilhada — a paleta vive em `lib/medalhas.js`. */
function MedalCounts({ medals }) {
  return <ContagemMedalhas medals={medals} incluirSegundoBronze />;
}

/** Pódio ÚNICO do sistema (o mesmo selo que o ranking público usa). */
function PositionBadge({ index }) {
  return <SeloPosicao posicao={index + 1} />;
}

function TotalCell({ value }) {
  return (
    <div className="flex items-center justify-end gap-1 text-[#D4AF37] font-bold">
      <TrendingUp size={16} />
      <span data-testid="total-points">{value}</span>
    </div>
  );
}

const th = 'micro-rotulo';
const td = 'text-white text-sm';

export default function FederativoRankingPage() {
  const { user } = useAuth();
  const canConfigure = ['super_admin', 'federation'].includes(user?.role);

  const [federations, setFederations] = useState([]);
  const [federationId, setFederationId] = useState('');
  const [seasons, setSeasons] = useState([]);
  const [season, setSeason] = useState('all'); // 'all' | season string
  const [modality, setModality] = useState('all'); // 'all' | 'gi' | 'nogi' | 'misto'
  const [view, setView] = useState('athletes'); // 'athletes' | 'teams'

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    federationApi
      .list()
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        setFederations(list);
        if (list.length > 0) setFederationId((prev) => prev || list[0]._id);
      })
      .catch(() => toast.error('Erro ao carregar federações'));
  }, []);

  useEffect(() => {
    if (!federationId) return;
    rankingFederativoApi
      .listSeasons(federationId)
      .then(({ data }) => setSeasons(Array.isArray(data) ? data : []))
      .catch(() => setSeasons([]));
  }, [federationId]);

  const loadRanking = useCallback(async () => {
    if (!federationId) return;
    setLoading(true);
    try {
      const params = {};
      if (season !== 'all') params.season = season;
      if (modality !== 'all') params.modality = modality;
      const { data } =
        view === 'teams'
          ? await rankingFederativoApi.getTeamRanking(federationId, params)
          : await rankingFederativoApi.getRanking(federationId, params);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao carregar ranking federativo');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [federationId, season, modality, view]);

  useEffect(() => {
    loadRanking();
  }, [loadRanking]);

  const rows = data?.rows || [];

  return (
    <PageContent>
      <div className="space-y-6" data-testid="federativo-ranking-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white font-['Chivo'] flex items-center gap-2">
              <Award size={24} className="text-[#D4AF37]" /> Ranking Federativo
            </h1>
            <p className="text-[#A1A1AA] mt-1 text-sm">
              Ranking acumulado por Federação/Liga e temporada — cada federação aplica suas próprias regras.
            </p>
          </div>
          {canConfigure && (
            <Button asChild variant="outline" className="border-[#27272A] text-[#A1A1AA] hover:text-white">
              <Link to="/ranking-federativo/regras" data-testid="go-to-rules-btn">
                <Settings2 size={16} className="mr-2" /> Configurar Regras
              </Link>
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="space-y-1.5 sm:w-64">
                <Label className="text-[#A1A1AA] text-xs">Federação/Liga</Label>
                <Select value={federationId || undefined} onValueChange={setFederationId}>
                  <SelectTrigger data-testid="ranking-federation-select" className="bg-[#1A1A1A] border-[#27272A] text-white">
                    <SelectValue placeholder="Selecione a federação" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                    {federations.map((f) => (
                      <SelectItem key={f._id} value={f._id} className="text-white hover:bg-[#27272A]">
                        <span className="flex items-center gap-2"><Building2 size={16} className="text-[#D4AF37]" /> {f.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 sm:w-48">
                <Label className="text-[#A1A1AA] text-xs">Temporada</Label>
                <Select value={season} onValueChange={setSeason}>
                  <SelectTrigger data-testid="ranking-season-select" className="bg-[#1A1A1A] border-[#27272A] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                    <SelectItem value="all" className="text-white hover:bg-[#27272A]">Todas as temporadas</SelectItem>
                    {seasons.map((s) => (
                      <SelectItem key={s._id} value={s.season} className="text-white hover:bg-[#27272A]">
                        {s.season}
                        {s.year_start != null && s.year_end != null ? ` (${s.year_start}–${s.year_end})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 sm:w-44">
                <Label className="text-[#A1A1AA] text-xs">Modalidade</Label>
                <Select value={modality} onValueChange={setModality}>
                  <SelectTrigger data-testid="ranking-modality-select" className="bg-[#1A1A1A] border-[#27272A] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                    <SelectItem value="all" className="text-white hover:bg-[#27272A]">Todas</SelectItem>
                    <SelectItem value="gi" className="text-white hover:bg-[#27272A]">Gi (Kimono)</SelectItem>
                    <SelectItem value="nogi" className="text-white hover:bg-[#27272A]">No-Gi</SelectItem>
                    <SelectItem value="misto" className="text-white hover:bg-[#27272A]">Misto</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* View toggle */}
              <div className="sm:ml-auto flex rounded-lg bg-[#1A1A1A] border border-[#27272A] p-1">
                <button
                  onClick={() => setView('athletes')}
                  data-testid="view-athletes-btn"
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    view === 'athletes' ? 'bg-[#D4AF37] text-black' : 'text-[#A1A1AA] hover:text-white'
                  }`}
                >
                  <Users size={16} /> Atletas
                </button>
                <button
                  onClick={() => setView('teams')}
                  data-testid="view-teams-btn"
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    view === 'teams' ? 'bg-[#D4AF37] text-black' : 'text-[#A1A1AA] hover:text-white'
                  }`}
                >
                  <Trophy size={16} /> Equipes
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rule versions used */}
        {data && (data.rule_versions_used?.length > 0 || data.team_points_sources_used?.length > 0) && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {(data.rule_versions_used || []).map((v) => (
              <span key={v} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#1A1A1A] border border-[#27272A] text-[#71717A]">
                <Settings2 size={12} /> Regra v{v}
              </span>
            ))}
            {/* FASE 4 — a agregação das equipes é SEMPRE por medalha. O que
                aparece como "team_points_sources_used" é o que ficou CONGELADO
                no histórico dos campeonatos (auditoria) e não é mais usado. */}
            <span
              data-testid="team-aggregation-badge"
              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#1A1A1A] border border-[#D4AF37]/40 text-[#E5C158]"
            >
              <Users size={12} /> Equipes: somente medalha
            </span>
            {(data.team_points_sources_used || [])
              .filter((src) => src !== 'medals_only')
              .map((src) => (
                <span
                  key={src}
                  title="Configuração congelada em campeonatos antigos — não é usada no cálculo de hoje"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#1A1A1A] border border-[#27272A] text-[#52525B]"
                >
                  <Users size={12} /> histórico: {src === 'athletes_with_fights' ? 'somente atletas com lutas' : 'todos os atletas pontuados'}
                </span>
              ))}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span></div>
        ) : !federationId ? (
          <Card className="bg-[#141414] border-[#27272A]">
            <CardContent>
              <EstadoVazio icone={Building2} titulo="Selecione uma federação" descricao="O Ranking Federativo é proprietário de cada Federação/Liga" />
            </CardContent>
          </Card>
        ) : rows.length === 0 ? (
          <Card className="bg-[#141414] border-[#27272A]">
            <CardContent>
              <EstadoVazio
                icone={Award}
                titulo="Nenhuma pontuação registrada"
                descricao="Campeonatos com Federação Organizadora geram pontos automaticamente a partir dos resultados esportivos (lutas efetivamente realizadas). BYE nunca pontua."
              />
            </CardContent>
          </Card>
        ) : view === 'athletes' ? (
          /* Athletes ranking */
          <Card className="bg-[#141414] border-[#27272A]">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table data-testid="athletes-ranking-table">
                  <TableHeader>
                    <TableRow className="border-[#27272A] hover:bg-transparent">
                      <TableHead className={`${th} w-12 text-center`}>#</TableHead>
                      <TableHead className={th}>Atleta</TableHead>
                      <TableHead className={`${th} text-center`}>Camp.</TableHead>
                      <TableHead className={`${th} text-center`}>Medalhas</TableHead>
                      <TableHead className={`${th} text-center`}>Pts Medalha</TableHead>
                      <TableHead className={`${th} text-center`}>Particip.</TableHead>
                      <TableHead className={`${th} text-center`}>Peso</TableHead>
                      <TableHead className={`${th} text-center`}>Lutas</TableHead>
                      <TableHead className={`${th} text-center`}>Performance</TableHead>
                      <TableHead className={`${th} text-right`}>TOTAL</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r, index) => (
                      <TableRow key={r.athlete_id} className="border-[#27272A]/40" data-testid={`athlete-row-${index + 1}`}>
                        <TableCell className="text-center"><PositionBadge index={index} /></TableCell>
                        <TableCell className={`${td} font-medium`}>{r.athlete_name}</TableCell>
                        <TableCell className={`${td} text-center text-[#A1A1AA]`}>{r.championships_disputed}</TableCell>
                        <TableCell className="text-center"><MedalCounts medals={r.medals} /></TableCell>
                        <TableCell className={`${td} text-center`}>{r.medal_points}</TableCell>
                        <TableCell className={`${td} text-center`}>{r.participation_points}</TableCell>
                        <TableCell className={`${td} text-center`}>{r.weight_bonus}</TableCell>
                        <TableCell className={`${td} text-center flex items-center justify-center gap-1`}>
                          <Swords size={12} className="text-[#71717A]" /> {r.fights_count}
                        </TableCell>
                        <TableCell className={`${td} text-center`}>{r.performance_points_bonus}</TableCell>
                        <TableCell className="text-right"><TotalCell value={r.total_points} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="text-micro text-[#71717A] p-3 border-t border-[#27272A]">
                TOTAL = medalha + participação (por luta efetiva) + peso do campeonato (bônus aditivo) + performance. Pontuação de luta segue a regra versionada de cada campeonato (congelada no snapshot).
              </p>
            </CardContent>
          </Card>
        ) : (
          /* Teams ranking */
          <Card className="bg-[#141414] border-[#27272A]">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table data-testid="teams-ranking-table">
                  <TableHeader>
                    <TableRow className="border-[#27272A] hover:bg-transparent">
                      <TableHead className={`${th} w-12 text-center`}>#</TableHead>
                      <TableHead className={th}>Equipe</TableHead>
                      <TableHead className={`${th} text-center`}>Atletas</TableHead>
                      <TableHead className={`${th} text-center`}>Medalhas</TableHead>
                      <TableHead className={`${th} text-center`}>Pts Medalha</TableHead>
                      <TableHead className={`${th} text-center`}>Particip.</TableHead>
                      <TableHead className={`${th} text-center`}>Peso</TableHead>
                      <TableHead className={`${th} text-center`}>Lutas</TableHead>
                      <TableHead className={`${th} text-center`}>Performance</TableHead>
                      <TableHead className={`${th} text-right`}>TOTAL</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r, index) => (
                      <TableRow key={r.team_id} className="border-[#27272A]/40" data-testid={`team-row-${index + 1}`}>
                        <TableCell className="text-center"><PositionBadge index={index} /></TableCell>
                        <TableCell className={`${td} font-medium`}>{r.team_name || r.team_id}</TableCell>
                        <TableCell className={`${td} text-center text-[#A1A1AA]`}>{r.athletes_count}</TableCell>
                        <TableCell className="text-center"><MedalCounts medals={r.medals} /></TableCell>
                        <TableCell className={`${td} text-center`}>{r.medal_points}</TableCell>
                        <TableCell className={`${td} text-center`}>{r.participation_points}</TableCell>
                        <TableCell className={`${td} text-center`}>{r.weight_bonus}</TableCell>
                        <TableCell className={`${td} text-center flex items-center justify-center gap-1`}>
                          <Swords size={12} className="text-[#71717A]" /> {r.fights_count}
                        </TableCell>
                        <TableCell className={`${td} text-center`}>{r.performance_points_bonus}</TableCell>
                        <TableCell className="text-right"><TotalCell value={r.total_points} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="text-micro text-[#71717A] p-3 border-t border-[#27272A]">
                Pontos da equipe = soma dos pontos de <span className="text-[#A1A1AA]">MEDALHA</span> dos atletas medalhistas
                (título de equipe por medalha) e ordem por medalha: ouro, prata, bronze. Nunca é uma pontuação paralela.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </PageContent>
  );
}
